import type { FiatPrice } from "@b2bportal/core-types";
import Big from "big.js";
import { Logger } from "@/utils/logging";

// --- Calculate Points Earned for Fiat ---
// Gets the points earned for a given fiat price.

// Q: Why don't we just use a simple multiplier for all fiat transactions?
// A: In the future we may want to have more complex rules for calculating
// points. For example, there might be incentives for using points above a
// certain threshold. This format will be more flexible for future changes.

// Q: Why is this hard coded instead of coming from the server?
// A: This is a temporary solution for launch. In the future, we can deliver
// the rules from the server so they can reflect the exact user, booking, etc.

const log = new Logger("calculatePointsEarnedForFiat");

export type ProductType = "hotel" | "package";

// The multiplier rule multiplies the value of the fiat price by a given factor.
export type PointsEarnRuleMultiplier = {
  type: "multiplier";
  value: number;
  productType: ProductType;
};
export type PointsEarnRuleBonus = {
  type: "bonus";
  value: number;
  productType: ProductType;
};

// We're currently only supporting the multiplier rule.
export type PointsEarnRule = PointsEarnRuleMultiplier | PointsEarnRuleBonus;

export type PointsEarnConfig = { rules?: PointsEarnRule[] };

// For launch, we're using a simple multiplier of 1 for all fiat transactions.
export const defaultPointsEarnConfig: PointsEarnConfig = {
  rules: [{ type: "multiplier", value: 1, productType: "hotel" }],
};

// Calculate the points earned for a given fiat price.
export const calculatePointsEarnedForFiat = (
  fiat: FiatPrice,
  productType: ProductType,
  config: PointsEarnConfig = defaultPointsEarnConfig
): number => {
  log.verbose("Calculating points earned for fiat", {
    fiat,
    productType,
    config,
  });

  if (fiat.currencyCode !== "USD") {
    const error = new Error(
      `Cannot calculate earn on non-USD amount (${fiat.currencyCode})`
    );
    log.error(error);
    throw error;
  }

  // Filter rules for the specific product type
  const applicableRules =
    config.rules?.filter((rule) => rule.productType === productType) ?? [];

  const fiatValue = new Big(fiat.value ?? 0);

  const result = applicableRules.reduce((acc, rule) => {
    switch (rule.type) {
      case "multiplier":
        return acc.times(rule.value);
      case "bonus":
        return acc.plus(rule.value);
      default:
        return acc;
    }
  }, fiatValue);

  const rounded = result.round(0, Big.roundHalfEven);

  // Some multipliers are not integers, so we need to round up.
  return rounded.toNumber();
};
